import { PixelBuffer } from "../pixel/PixelBuffer";
import type { Selection } from "../selection/Selection";
import type { Filter } from "./Filter";

export interface WaveParams {
  numGenerators: number;
  wavelength: number;
  amplitude: number;
  scale: number;
}

function clamp8(v: number): number {
  return v < 0 ? 0 : v > 255 ? 255 : v;
}

export const Wave: Filter<WaveParams> = {
  id: "wave",
  displayName: "Wave",
  defaultParams: { numGenerators: 3, wavelength: 50, amplitude: 20, scale: 100 },

  apply(input: PixelBuffer, params: WaveParams, _selection: Selection | null): PixelBuffer {
    const w = input.width;
    const h = input.height;
    const out = new PixelBuffer(w, h, input.mode);
    const src = input.data;
    const dst = out.data;

    if (w === 0 || h === 0) { dst.set(src); return out; }

    const numGen = Math.max(1, Math.round(params.numGenerators));
    const wl = Math.max(1, params.wavelength);
    const amp = params.amplitude * params.scale / 100;

    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        let dx = 0, dy = 0;
        for (let g = 0; g < numGen; g++) {
          const phase = (g / numGen) * Math.PI * 2;
          dx += Math.sin(y * Math.PI * 2 / wl + phase) * amp;
          dy += Math.cos(x * Math.PI * 2 / wl + phase) * amp;
        }
        const sx = Math.round(x + dx / numGen);
        const sy = Math.round(y + dy / numGen);

        const o = (y * w + x) * 4;
        if (sx >= 0 && sx < w && sy >= 0 && sy < h) {
          const si = (sy * w + sx) * 4;
          dst[o] = src[si]!; dst[o + 1] = src[si + 1]!; dst[o + 2] = src[si + 2]!; dst[o + 3] = src[si + 3]!;
        } else {
          dst[o] = src[o]!; dst[o + 1] = src[o + 1]!; dst[o + 2] = src[o + 2]!; dst[o + 3] = src[o + 3]!;
        }
      }
    }
    return out;
  },
};
